/**
 * SegmentPracticeCard — v2 inline practice block injected after a lesson
 * segment.
 *
 * For one `## ` segment, surfaces that segment's assets right where the concept
 * was just taught:
 *   - NL-debug checks (interactive) — reused via <DebugFeedbackBlock>. The
 *     manifest references checks by `check_id`; the debug-list endpoint returns
 *     {check_id, exercise_id}, so we map check_id → exercise_id here.
 *   - Walkthrough function references — the worked-solution functions relevant
 *     to this segment, with a link to the full runnable file.
 *   - A "Lab" badge when the segment carries a hands-on lab.
 *
 * Renders nothing when the segment has no assets to show (e.g. the intro
 * segments — Objectives/Prerequisites/Overview), so the lesson stays clean.
 *
 * `ownedCheckIds` is the de-duplicated subset assigned to THIS segment: a check
 * tagged to several segments renders only in its first one, since each check is
 * a stateful interactive widget that should appear exactly once.
 */

import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ScienceIcon from "@mui/icons-material/Science";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  DebugExerciseSummary,
  ModuleManifestSegment,
  listDebugExercises,
} from "../api/client";
import DebugFeedbackBlock from "./DebugFeedbackBlock";
import WalkthroughViewer from "./WalkthroughViewer";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface SegmentPracticeCardProps {
  slug: string;
  segment: ModuleManifestSegment;
  /** Check ids assigned to this segment (already de-duplicated across segments). */
  ownedCheckIds: string[];
}

export default function SegmentPracticeCard({
  slug,
  segment,
  ownedCheckIds,
}: SegmentPracticeCardProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  // Shared, cached across every card on the page (same query key as the legacy
  // DebugExercisesBlock) — fetched once, mapped per segment.
  const { data: debugList } = useQuery<DebugExerciseSummary[]>({
    queryKey: ["debug-exercises", slug],
    queryFn: () => listDebugExercises(slug),
    retry: false,
  });

  const walkthroughFns = segment.walkthrough_fns ?? [];
  const hasLab = segment.has_lab ?? false;

  // Resolve owned check_ids → exercise_ids via the debug list.
  const exerciseIds = ownedCheckIds
    .map((cid) => debugList?.find((d) => d.check_id === cid)?.exercise_id)
    .filter((x): x is string => Boolean(x));

  const hasChecks = exerciseIds.length > 0;
  const hasWalkthrough = walkthroughFns.length > 0;

  // Nothing to show for this segment → render nothing (keeps intro segments clean).
  if (!hasChecks && !hasWalkthrough && !hasLab) return null;

  return (
    <Box
      sx={{
        my: 4,
        pl: 2.5,
        py: 0.5,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" gap={0.5}>
        <FitnessCenterIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: theme.palette.primary.main,
          }}
        >
          {t("SegmentPracticeCard.practice")}
        </Typography>
        {hasLab && (
          <Chip
            icon={<ScienceIcon sx={{ fontSize: "0.85rem !important" }} />}
            label={t("SegmentPracticeCard.lab")}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.68rem",
              fontWeight: 500,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          />
        )}
      </Stack>

      {/* Walkthrough function references */}
      {hasWalkthrough && (
        <Box mb={hasChecks ? 2 : 0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {t("SegmentPracticeCard.workedSolution")}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 0.5 }}>
            {walkthroughFns.map((fn) => (
              <Chip
                key={fn}
                label={`${fn}()`}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily:
                    '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: "0.72rem",
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: "text.primary",
                }}
              />
            ))}
          </Stack>
          {/* In-app, tier-gated source — replaces the old public GitHub link. */}
          <WalkthroughViewer slug={slug} />
        </Box>
      )}

      {/* Interactive NL-debug checks for this segment */}
      {hasChecks && (
        <Stack spacing={2}>
          {exerciseIds.map((exId) => (
            <DebugFeedbackBlock key={exId} slug={slug} exerciseId={exId} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
